import { closestStudy } from './closestStudy.js';
import { boundedRowStats, initialHrBounds } from './hrBounds.js';
import { reportScene, scenePdf, validateMetadata } from './hrRender.js';
import { parseRep } from './parseRep.js';
import { insertHrPage, matchesStudyPdf, readReport, scanReportPdfs } from './pdfInsert.js';
import { openStudyFiles } from './studyFiles.js';
import { studyMatchStatus } from './studyMatchStatus.js';

export const outputName = (name) => name.replace(/\.pdf$/i, '_HR.pdf');
export const sourceStudyId = (name) =>
  /_hr/i.test(name) ? null : /^(\d+)(?:[a-z][a-z0-9]*)?\.pdf$/i.exec(name)?.[1] || null;
export function eligibleReports(reports) {
  const names = new Set(reports.map((report) => report.name.toLowerCase()));
  return reports.filter(
    (report) => sourceStudyId(report.name) && !names.has(outputName(report.name).toLowerCase())
  );
}
const revision = (file) => (file ? `${file.size}:${file.lastModified}` : null);
export function buildAutoModel(files, settings, signal) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./hrWorker.js', import.meta.url), { type: 'module' });
    const finish = (error, model) => {
      worker.terminate();
      signal.removeEventListener('abort', abort);
      if (error) reject(error);
      else resolve(model);
    };
    const abort = () => finish(new Error('Auto create stopped.'));
    signal.addEventListener('abort', abort, { once: true });
    worker.addEventListener(
      'message',
      ({ data }) => finish(data.error ? new Error(data.error) : null, data.model),
      { once: true }
    );
    worker.addEventListener('error', () => finish(new Error('Unable to run QRS worker.')), {
      once: true,
    });
    if (signal.aborted) {
      abort();
      return;
    }
    worker.postMessage({
      studyKey: files.id,
      files: [files.main, files.a, files.b],
      sampleRate: settings.rate,
      repair: settings.repair,
    });
  });
}
export async function createAutoReport(report, study, settings, signal, build = buildAutoModel) {
  const files = await openStudyFiles(study, () => signal.aborted);
  const parsed = parseRep(await files.rep.arrayBuffer(), study.stem);
  if (!matchesStudyPdf(report.name, parsed.meta.study))
    throw new Error('REP Study ID does not match PDF filename.');
  validateMetadata(parsed.meta);
  const source = await readReport(report);
  if (!source.summary?.recognized || !source.heartRateBox?.recognized)
    throw new Error('Summary or Heart Rate box is unreadable; manual review required.');
  const limits = source.summary;
  if (
    !Number.isInteger(limits.min) ||
    !Number.isInteger(limits.max) ||
    limits.min < 0 ||
    limits.min > limits.max
  )
    throw new Error('Invalid Summary HR values.');
  const model = await build(files, settings, signal);
  if (signal.aborted) throw new Error('Auto create stopped.');
  const rows = boundedRowStats(model.rowStats, { minFloor: limits.min, maxCeiling: limits.max });
  if (!rows.some((row) => row.min === limits.min) || !rows.some((row) => row.max === limits.max))
    throw new Error('No graph HR values available.');
  const meta = {
    ...parsed.meta,
    ...initialHrBounds(model.rowStats),
    minFloor: String(limits.min),
    maxCeiling: String(limits.max),
  };
  const hr = scenePdf(reportScene(model, meta));
  const merged = await insertHrPage(
    source.bytes,
    hr,
    settings.page,
    limits,
    true,
    settings.includeStripIndex !== false
  );
  const latest = await openStudyFiles(study, () => signal.aborted);
  if (['rep', 'main', 'a', 'b'].some((key) => revision(files[key]) !== revision(latest[key])))
    throw new Error('Study changed during generation; retrying next check.');
  return merged;
}

const runStates = new WeakMap();

// One sequential pass. Names are enumerated; PDF contents are read only for candidates.
export async function autoCreatePass({
  directory,
  studies,
  refreshStudies,
  hours,
  settings,
  signal,
  seen,
  reportStatus,
  create = createAutoReport,
  inspect = readReport,
  collectReports = null,
  getStudies = null,
  now = Date.now(),
}) {
  const reports = collectReports
    ? await collectReports(seen, signal)
    : await scanReportPdfs(directory);
  let run = runStates.get(seen);
  if (!run) {
    run = {
      initialNames: new Set(reports.map((report) => report.name)),
      cutoff: now - hours * 3600000,
    };
    runStates.set(seen, run);
  }
  let available = getStudies ? getStudies() : studies;
  let refreshed = false;
  for (const report of eligibleReports(reports)) {
    if (signal.aborted) return;
    let attemptKey = null;
    try {
      const file = await report.handle.getFile();

      const key = revision(file);
      const previous = seen.get(report.name);
      if (
        !previous &&
        !report.arrived &&
        run.initialNames.has(report.name) &&
        file.lastModified < run.cutoff
      ) {
        seen.set(report.name, { key, excluded: true });
        continue;
      }
      if (previous?.key === key && (previous.failed || previous.excluded || previous.completed))
        continue;
      if (!file.size) {
        seen.set(report.name, { key });
        continue;
      }
      attemptKey = key;
      seen.set(report.name, { key });
      // Require identical size/mtime on successive checks, allowing copies to settle.
      if (previous?.key !== key) continue;
      try {
        await directory.getFileHandle(outputName(report.name));
        seen.set(report.name, { key, completed: true });
        continue;
      } catch (error) {
        if (error.name !== 'NotFoundError') throw error;
      }
      // Classify first so Event/MCT PDFs never masquerade as missing REP matches.
      await inspect(report);
      const id = sourceStudyId(report.name);
      let selected = closestStudy(available, id, file.lastModified);
      if (!selected?.ready && !refreshed && !collectReports) {
        available = (await refreshStudies()) || available;
        refreshed = true;
        selected = closestStudy(available, id, file.lastModified);
      }
      if (!selected?.ready) {
        seen.set(report.name, { key, failed: true, unmatched: true, studyId: id });
        reportStatus(
          report.name,
          `${studyMatchStatus(selected ? [selected] : available, id)} Retries when that study changes.`
        );
        continue;
      }
      const repTime = selected.repRevision?.lastModified ?? selected.rep?.lastModified;
      const differenceHours = Math.abs(repTime - file.lastModified) / 3600000;
      reportStatus(
        report.name,
        `Creating using ${selected.folder || ''}/${selected.rep?.name || selected.stem}. Closest REP/PDF modification times: ${differenceHours.toFixed(2)} hours apart${differenceHours > 72 ? ' (over 3 days)' : ''}.`
      );
      const bytes = await create(report, selected, settings, signal);
      if (signal.aborted) return;
      if (revision(await report.handle.getFile()) !== key)
        throw new Error('PDF changed during generation; retrying next check.');
      const name = outputName(report.name);
      // Recheck immediately before creating; never overwrite an existing output.
      try {
        await directory.getFileHandle(name);
        reportStatus(report.name, 'Output already exists.');
        continue;
      } catch (error) {
        if (error.name !== 'NotFoundError') throw error;
      }
      if (signal.aborted) return;
      const destination = await directory.getFileHandle(name, { create: true });
      let writable;
      try {
        writable = await destination.createWritable();
        if (signal.aborted) throw new Error('Auto create stopped.');
        await writable.write(bytes);
        if (signal.aborted) throw new Error('Auto create stopped.');
        await writable.close();
      } catch (error) {
        if (writable) await writable.abort().catch(() => {});
        await directory.removeEntry(name).catch(() => {});
        throw error;
      }
      seen.set(report.name, { key, completed: true });
      reportStatus(report.name, `Saved ${name}`);
    } catch (error) {
      if (signal.aborted) return;
      seen.set(report.name, { key: attemptKey, failed: true });
      reportStatus(
        report.name,
        error.message + ' Skipped until the PDF changes or Auto create restarts.'
      );
      if (error.name === 'NotAllowedError') throw error;
    }
  }
}
